import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// NOTE (2026-09): the System A embedding/query state (embedding jobs, corpus stats,
// search results) was retired together with the backend endpoints. This store now
// tracks the KSearch (Phase 1) conversation state and Phase 2 patent acquisition.

export type WorkflowPhase = 'ksearch' | 'ksearch_complete' | 'acquisition';

export type SearchCompleteness = 'incomplete' | 'sufficient' | 'comprehensive';

export interface KSearchCoverage {
  total_patents: number;
  cpc_classes_found: string[];
  keyword_strategies_explored: string[];
}

interface AcquisitionProgress {
  acquired_count: number;
  failed_count: number;
  total_patents: number;
  progress_percent: number;
}

interface SemanticSearchState {
  subProjectId: string | null;

  // KSearch Phase 1 state
  currentPhase: WorkflowPhase;
  kSearchCoverage: KSearchCoverage | null;
  searchCompleteness: SearchCompleteness;
  innerLoopActive: boolean;

  // Acquisition state
  acquisitionStatus: 'not_started' | 'in_progress' | 'completed' | 'failed';
  acquisitionProgress: AcquisitionProgress | null;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Actions
  initializeSearch: (subProjectId: string) => void;

  // KSearch Phase 1 actions
  setPhase: (phase: WorkflowPhase) => void;
  updateKSearchCoverage: (coverage: Partial<KSearchCoverage>) => void;
  setSearchCompleteness: (completeness: SearchCompleteness) => void;
  startInnerLoop: () => void;
  completeInnerLoop: () => void;

  // Acquisition actions
  setAcquisitionStatus: (status: SemanticSearchState['acquisitionStatus']) => void;
  setAcquisitionProgress: (progress: AcquisitionProgress) => void;

  // UI actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  subProjectId: null,
  currentPhase: 'ksearch' as WorkflowPhase,
  kSearchCoverage: null,
  searchCompleteness: 'incomplete' as SearchCompleteness,
  innerLoopActive: false,
  acquisitionStatus: 'not_started' as const,
  acquisitionProgress: null,
  isLoading: false,
  error: null,
};

const useSemanticSearchStore = create<SemanticSearchState>()(
  devtools(
    (set) => ({
      ...initialState,

      initializeSearch: (subProjectId: string) =>
        set({
          ...initialState,
          subProjectId,
        }),

      // KSearch Phase 1 actions
      setPhase: (phase) =>
        set({ currentPhase: phase }),

      updateKSearchCoverage: (coverage) =>
        set((state) => ({
          kSearchCoverage: state.kSearchCoverage
            ? { ...state.kSearchCoverage, ...coverage }
            : {
                total_patents: coverage.total_patents || 0,
                cpc_classes_found: coverage.cpc_classes_found || [],
                keyword_strategies_explored: coverage.keyword_strategies_explored || [],
              },
        })),

      setSearchCompleteness: (completeness) =>
        set({ searchCompleteness: completeness }),

      startInnerLoop: () =>
        set({ innerLoopActive: true }),

      completeInnerLoop: () =>
        set({ innerLoopActive: false }),

      // Acquisition actions
      setAcquisitionStatus: (status) =>
        set({ acquisitionStatus: status }),

      setAcquisitionProgress: (progress) =>
        set({ acquisitionProgress: progress }),

      // UI actions
      setLoading: (loading) =>
        set({ isLoading: loading }),

      setError: (error) =>
        set({ error }),

      // Reset
      reset: () =>
        set(initialState),
    }),
    { name: 'SemanticSearchStore' }
  )
);

export default useSemanticSearchStore;
